import {
  computed,
  ref,
  toValue,
  type ComputedRef,
  type MaybeRefOrGetter,
  type Ref,
} from "vue";
import { useFieldsetTouchTracker } from "./useFieldsetTouchTracker";

export interface UseFieldsetValidationKitOptions {
  /**
   * Consumer-owned submit state. The kit reads this value but never mutates it.
   */
  submitted: MaybeRefOrGetter<boolean>;
  /**
   * Returns the real validity of the fieldset when validation is active.
   */
  isValid: () => boolean;
}

/**
 * API returned by {@link useFieldsetValidationKit}.
 */
export interface UseFieldsetValidationKitApi {
  /**
   * Becomes true after the first focus transition that leaves the fieldset.
   */
  touched: Ref<boolean>;
  /**
   * Delayed validation gate. True when either submitted or touched is true.
   */
  fieldsetValidate: ComputedRef<boolean>;
  /**
   * Forwards fieldset focusout events to the internal touch tracker.
   */
  onFocusout: (event: FocusEvent) => void;
  /**
   * Clears touched state and suppresses one immediate post-reset focusout.
   *
   * This avoids re-activating delayed validation from reset-related focus
   * transitions (for example, clicking Reset while an input is focused).
   */
  reset: () => void;
  /**
   * Wraps a field-level validity source with delayed validation behavior.
   */
  createFieldValidity: (
    isValid: MaybeRefOrGetter<boolean>,
  ) => ComputedRef<boolean>;
  /**
   * Builds a named map of delayed field validity refs for ergonomic form setup.
   */
  createFieldValidationsMap: <
    T extends Record<string, MaybeRefOrGetter<boolean>>,
  >(
    isValidMap: T,
  ) => { [K in keyof T]: ComputedRef<boolean> };
  /**
   * Moves focus to the first invalid Kendo control found within the provided form.
   */
  focusFirstInvalidField: (
    formRef: MaybeRefOrGetter<HTMLFormElement | null>,
  ) => void;
}

/**
 * Orchestrates delayed fieldset validation using submit state and touch state.
 *
 * Intended as the primary form-level API for gated validity UX:
 * - validation displays after submit or first fieldset exit
 * - field-level validity can be composed through helper factories
 * - invalid focus and reset behavior are handled consistently
 *
 * @param fieldsetRef Reference to the fieldset whose focus transitions are tracked.
 * @param options Consumer-provided submit state and base validity function.
 * @returns Validation kit API for wiring fieldset-level and field-level behavior.
 */
export function useFieldsetValidationKit(
  fieldsetRef: Ref<HTMLFieldSetElement | null>,
  options: UseFieldsetValidationKitOptions,
): UseFieldsetValidationKitApi {
  const touched = ref(false);
  // After reset, browser/UI focus can emit an immediate focusout that does not
  // represent a user intentionally leaving the fieldset. Ignore exactly one
  // focusout to prevent delayed validation from re-arming right away.
  const suppressNextTouch = ref(false);
  const fieldsetValidate = computed(
    () => toValue(options.submitted) || touched.value,
  );

  // Computes the effective validity of the fieldset, taking into account the
  // delayed validation gate.
  const fieldsetIsValid = computed(() => {
    return !fieldsetValidate.value || options.isValid();
  });

  // Initializes the internal touch tracker for the fieldset.
  const tracker = useFieldsetTouchTracker(fieldsetRef, {
    isValid: () => fieldsetIsValid.value,
  });

  // Creates a delayed validity ref for a single field based on the fieldset's
  // validation state.
  function createFieldValidity(isValid: MaybeRefOrGetter<boolean>) {
    return computed(() => !fieldsetValidate.value || toValue(isValid));
  }

  // Builds a named map of delayed field validity refs from the provided validity map.
  // (e.g. { firstName: () => firstNameValid, lastName: () => lastNameValid })
  // show example map?
  // Example usage:
  // const fieldValidations = createFieldValidationsMap({
  //   firstName: () => firstNameValid,
  //   lastName: () => lastNameValid,
  // });
  // The returned object will have the same keys as the input map, but each value will
  // be a computed ref that reflects the delayed validation state.
  function createFieldValidationsMap<
    T extends Record<string, MaybeRefOrGetter<boolean>>,
  >(isValidMap: T) {
    return Object.fromEntries(
      Object.entries(isValidMap).map(([key, isValid]) => [
        key,
        createFieldValidity(isValid),
      ]),
    ) as { [K in keyof T]: ComputedRef<boolean> };
  }

  // Focuses the first invalid field within the provided FORM.
  // TODO: Leaving this here for now as a convenience, but it does not belong
  // in the core validation kit. This should be its own composable or utility
  // function.  I may move this to USAddress component-specific logic.
  function focusFirstInvalidField(
    formRef: MaybeRefOrGetter<HTMLFormElement | null>,
  ) {
    const form = toValue(formRef);
    if (!form) return;

    const firstInvalid = form.querySelector(
      '.k-invalid input, .k-invalid[tabindex="0"]',
    ) as HTMLElement | null;

    firstInvalid?.focus();
  }

  // Forwards fieldset focusout events to the internal touch tracker.
  function onFocusout(event: FocusEvent) {
    if (suppressNextTouch.value) {
      // Consume the first post-reset focusout caused by reset/click focus
      // churn.
      suppressNextTouch.value = false;
      return;
    }

    tracker.onFocusout(event);

    if (tracker.touched.value) {
      touched.value = true;
    }
  }

  // Resets the touched state and suppresses one immediate post-reset focusout.
  function reset() {
    touched.value = false;
    suppressNextTouch.value = true;
    tracker.reset();
  }

  return {
    touched,
    fieldsetValidate,
    onFocusout,
    reset,
    createFieldValidity,
    createFieldValidationsMap,
    focusFirstInvalidField,
  };
}
